import { Link } from "react-router-dom";
import OrgNode from "./OrgNode.jsx";
import { isJobDemandSide, middleTierLabel, organizationSideLabel } from "./workerLineShared.js";
import OrgConnectorLayer from "./OrgConnectorLayer.jsx";
import { statusBadgeVariant } from "./workerLineShared.js";

// P156: the two sides of a Super Agent line count different things, so the
// stat line under each node has to say what that line is actually made of.
// A job-side line ends in employers - showing it "0 คน" would read as an empty
// line rather than a line of a different kind.
function superAgentStat(superAgent) {
  const side = organizationSideLabel(superAgent.organizationSide);
  const tier = middleTierLabel(superAgent.organizationSide);
  if (isJobDemandSide(superAgent.organizationSide)) {
    return `${side} · ${superAgent.plcCount} ${tier} · ${superAgent.agentCount} Agent`;
  }
  return `${side} · ${superAgent.plcCount} ${tier} · ${superAgent.agentCount} Agent · ${superAgent.workerCount} คน`;
}

function plcStat(plc) {
  if (isJobDemandSide(plc.organizationSide)) {
    return `${plc.agentCount} Agent`;
  }
  return `${plc.agentCount} Agent · ${plc.workerCount} คน`;
}

function agentStat(agent) {
  if (isJobDemandSide(agent.organizationSide)) {
    return `${agent.employerCount ?? 0} นายจ้าง`;
  }
  return `${agent.workerCount} คน · ${agent.activeAssignmentCount} active`;
}

function isSelected(selectedNode, kind, id) {
  return selectedNode?.kind === kind && selectedNode.id === id;
}

/** Network/Compact tree branch: a Super Agent node, its own equal-width PLC lane below it (grid `minmax(0, 1fr)` tracks - a professional aligned-columns look), and each PLC's own equal-width Agent lane below that. The lane grid governs LAYOUT only; the connector above each row (OrgConnectorLayer) independently measures the real rendered position of every node in that row via getBoundingClientRect, so every drop line lands on its target's true center even where a lane's content (e.g. an unusually long name, or a sibling forcing a wider shared track) shifts a node away from where an equal-fraction guess would expect it - see OrgConnectorLayer's own doc comment. */
function SuperAgentBranch({ superAgent, selectedNode, onSelectNode }) {
  const plcs = superAgent.plcs;
  const laneCount = plcs.length || 1;

  return (
    <div className="wx-oc-cluster">
      <OrgNode
        kind="superAgent"
        name={superAgent.displayName}
        code={superAgent.superAgentCode}
        status={superAgent.status}
        statLine={superAgentStat(superAgent)}
        selected={isSelected(selectedNode, "superAgent", superAgent.superAgentOrganizationId)}
        onClick={() => onSelectNode("superAgent", superAgent.superAgentOrganizationId)}
      />
      <div className="wx-oc-branch">
        <OrgConnectorLayer />
        <div className="wx-oc-row" style={{ gridTemplateColumns: `repeat(${laneCount}, minmax(0, 1fr))` }}>
          {plcs.length === 0 ? (
            <OrgNode href={`/backoffice/worker-line/plcs?createForSuperAgentId=${superAgent.superAgentOrganizationId}`} ghostLabel={`สร้าง ${middleTierLabel(superAgent.organizationSide)}`} />
          ) : (
            plcs.map((plc) => (
              <PlcBranch key={plc.plcOrganizationId} plc={plc} superAgentId={superAgent.superAgentOrganizationId} selectedNode={selectedNode} onSelectNode={onSelectNode} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function PlcBranch({ plc, superAgentId, selectedNode, onSelectNode }) {
  const agents = plc.agents;
  const laneCount = agents.length || 1;

  return (
    <div className="wx-oc-cluster">
      <OrgNode
        kind="plc"
        name={plc.displayName}
        code={plc.plcCode}
        status={plc.status}
        statLine={plcStat(plc)}
        selected={isSelected(selectedNode, "plc", plc.plcOrganizationId)}
        onClick={() => onSelectNode("plc", plc.plcOrganizationId)}
      />
      <div className="wx-oc-branch">
        <OrgConnectorLayer />
        <div className="wx-oc-row" style={{ gridTemplateColumns: `repeat(${laneCount}, minmax(0, 1fr))` }}>
          {agents.length === 0 ? (
            <OrgNode href={`/backoffice/worker-line/agents?createForSuperAgentId=${superAgentId}&createForPlcId=${plc.plcOrganizationId}`} ghostLabel="สร้าง Agent" />
          ) : (
            agents.map((agent) => (
              <OrgNode
                key={agent.agentId}
                kind="agent"
                name={agent.displayName}
                code={agent.agentCode}
                status={agent.status}
                statLine={agentStat(agent)}
                selected={isSelected(selectedNode, "agent", agent.agentId)}
                onClick={() => onSelectNode("agent", agent.agentId)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function NetworkCanvas({ tree, selectedNode, onSelectNode, compact }) {
  const laneCount = tree.length || 1;

  return (
    <div className={`wx-oc-scroll${compact ? " wx-oc-scroll--compact" : ""}`}>
      <div className="wx-oc-inner">
        <div className="wx-oc-tier wx-oc-tier--root">
          <OrgNode kind="root" name="SuperAdmin" />
        </div>
        <div className="wx-oc-branch">
          <OrgConnectorLayer />
          <div className="wx-oc-row" style={{ gridTemplateColumns: `repeat(${laneCount}, minmax(0, 1fr))` }}>
            {tree.map((superAgent) => (
              <SuperAgentBranch key={superAgent.superAgentOrganizationId} superAgent={superAgent} selectedNode={selectedNode} onSelectNode={onSelectNode} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ListRow({ kind, name, code, status, statLine, indent, selected, onClick, href, ghostLabel }) {
  if (href) {
    return (
      <Link to={href} className={`wx-oc-list-row wx-oc-list-row--ghost wx-oc-list-row--indent-${indent}`}>
        + {ghostLabel}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={`wx-oc-list-row wx-oc-list-row--indent-${indent}${selected ? " wx-oc-list-row--selected" : ""}`}
      onClick={onClick}
    >
      <span className="wx-oc-list-row-main">
        <strong>{name}</strong>
        <span className="wx-oc-list-row-code">{code}</span>
      </span>
      {status && <span className={`wx-badge wx-badge--${statusBadgeVariant(status)}`}>{status}</span>}
      <span className="wx-oc-list-row-stat">{statLine}</span>
    </button>
  );
}

function ListFallback({ tree, selectedNode, onSelectNode }) {
  return (
    <div className="wx-oc-list" role="list" aria-label="รายการผังสายงานแบบ List fallback">
      <div className="wx-oc-list-row wx-oc-list-row--root wx-oc-list-row--indent-0">SuperAdmin</div>
      {tree.map((superAgent) => (
        <div key={superAgent.superAgentOrganizationId}>
          <ListRow
            kind="superAgent"
            name={superAgent.displayName}
            code={superAgent.superAgentCode}
            status={superAgent.status}
            statLine={superAgentStat(superAgent)}
            indent={1}
            selected={isSelected(selectedNode, "superAgent", superAgent.superAgentOrganizationId)}
            onClick={() => onSelectNode("superAgent", superAgent.superAgentOrganizationId)}
          />
          {superAgent.plcs.length === 0 ? (
            <ListRow indent={2} href={`/backoffice/worker-line/plcs?createForSuperAgentId=${superAgent.superAgentOrganizationId}`} ghostLabel={`สร้าง ${middleTierLabel(superAgent.organizationSide)}`} />
          ) : (
            superAgent.plcs.map((plc) => (
              <div key={plc.plcOrganizationId}>
                <ListRow
                  kind="plc"
                  name={plc.displayName}
                  code={plc.plcCode}
                  status={plc.status}
                  statLine={plcStat(plc)}
                  indent={2}
                  selected={isSelected(selectedNode, "plc", plc.plcOrganizationId)}
                  onClick={() => onSelectNode("plc", plc.plcOrganizationId)}
                />
                {plc.agents.length === 0 ? (
                  <ListRow
                    indent={3}
                    href={`/backoffice/worker-line/agents?createForSuperAgentId=${superAgent.superAgentOrganizationId}&createForPlcId=${plc.plcOrganizationId}`}
                    ghostLabel="สร้าง Agent"
                  />
                ) : (
                  plc.agents.map((agent) => (
                    <ListRow
                      key={agent.agentId}
                      kind="agent"
                      name={agent.displayName}
                      code={agent.agentCode}
                      status={agent.status}
                      statLine={agentStat(agent)}
                      indent={3}
                      selected={isSelected(selectedNode, "agent", agent.agentId)}
                      onClick={() => onSelectNode("agent", agent.agentId)}
                    />
                  ))
                )}
              </div>
            ))
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The main hierarchy board (Prompt 137): a real SuperAdmin -> Super Agent ->
 * PLC -> Agent network with SVG connectors in "network"/"compact" mode
 * (same DOM/CSS, "compact" only shrinks density via a modifier class - see
 * .wx-oc-canvas--compact in workerLine.css), or a flat indented list in
 * "list" mode for accessibility/very dense scopes. `.wx-oc-scroll` centers
 * a tree narrower than the panel and switches to a contained horizontal
 * scrollbar (never page-level overflow) once it is not - see that class's
 * own CSS comment for the `width: max-content; margin: 0 auto` mechanism.
 */
export default function OrgChartCanvas({ tree, viewMode, selectedNode, onSelectNode }) {
  if (viewMode === "list") {
    return <ListFallback tree={tree} selectedNode={selectedNode} onSelectNode={onSelectNode} />;
  }

  return <NetworkCanvas tree={tree} selectedNode={selectedNode} onSelectNode={onSelectNode} compact={viewMode === "compact"} />;
}
